/**
 * Checkout restored from a saved cart (abandoned_trips) instead of from the
 * page before it: a recovery-email link, a refresh, a new tab, a cold app
 * start, coming back from sign-in or a Reserve top-up.
 *
 * Why (audit 2026-10-27): of 599 emailed carts only 7% resumed at the fare
 * the customer chose. The restore read `cart.booking_option` and
 * `cart.payment_method`, columns abandoned_trips doesn't have, so the fare was
 * dropped and every cart came back as "installments". Review then priced the
 * trip from the search card, adding the return card's price to an outbound
 * card that already held the round-trip total: 80% overcharged (often 2x),
 * 12.5% undercharged by $816 on average ($9,388 at most). Both live inside
 * flight_data: `bookingOption` and `_payment_method`.
 *
 * A restored cart is priced only from its saved fare, and only when that fare
 * is the airline's own fare for these exact flights
 * (supabase/functions/_shared/fare-option-integrity.js). Otherwise the
 * customer picks the fare again (fare options for the same flights, when the
 * cart still has what that page needs), or searches again.
 */
import { checkFareOptionIntegrity } from '../../supabase/functions/_shared/fare-option-integrity.js';
import { writePassengerParams } from './passengerTypes.js';
import { CART_DISCOUNT_FIELDS } from './discountCodes.js';
import { withoutRetiredMilesMethod } from './index';

/** Booking-flow state from a cart row: flight_data carries the selected flight
 *  with the return leg, the search, the fare and the payment method inside it. */
export function bookingStateFromCart(cart) {
  const flightData = cart?.flight_data || {};
  let returnFlight = flightData._returnFlight || null;
  const searchParams = flightData._searchParams || null;

  const selectedFlight = { ...flightData };
  delete selectedFlight._returnFlight;
  delete selectedFlight._searchParams;

  // A merged round trip (2+ slices, no _returnFlight): split the return back out.
  if (!returnFlight && selectedFlight.slices?.length > 1) {
    const returnSlices = selectedFlight.slices.slice(1);
    if (returnSlices.some((slice) => slice?.segments?.length > 0)) {
      returnFlight = { ...selectedFlight, slices: returnSlices };
    }
  }

  const bookingOption = cart?.booking_option || flightData.bookingOption || null;
  return {
    searchParams,
    selectedFlight: Object.keys(selectedFlight).length > 0 ? selectedFlight : null,
    returnFlight,
    bookingOption,
    passengers: cart?.passengers || [],
    // A cart saved with a retired Miles method ("Miles", "Miles +
    // Installments") resumes on installments; Miles are applied at checkout.
    paymentMethod: withoutRetiredMilesMethod(cart?.payment_method || flightData._payment_method) || 'installments',
    // Without this the flag fell back to false on every rehydration
    // while paymentMethod stayed 'installments', which is exactly the
    // combination Review auto-upgrades to Defera Zero.
    choseRefundableInstallments: flightData._chose_refundable_installments === true,
  };
}

/** The itinerary's slices as Review merges them: outbound, then the return. */
export function itinerarySlices(selectedFlight, returnFlight) {
  const out = Array.isArray(selectedFlight?.slices) ? selectedFlight.slices : [];
  const back = Array.isArray(returnFlight?.slices) ? returnFlight.slices : [];
  return out.length === 1 && back.length > 0 ? [...out, ...back] : out;
}

/** A flight from a flight search, whose price is the fare picked on the
 *  fare-options page (never the search card). An uploaded itinerary has none. */
export function flightNeedsChosenFare(flight) {
  return !!(flight && (
    flight.booking_token || flight.departure_token || flight.serpapi_source
    || flight.duffel_source || flight.mondee_source || flight.searchapi_source
  ));
}

/** Keys a cart adds to the offer it saves. None of them belong to a fresh
 *  fare pick (an old payment schedule would ride into the next cart's emails). */
const CART_ONLY_KEYS = [
  'bookingOption', 'payment_schedule', 'pricing_snapshot', 'defera_booking_reference',
  'seat_selections_detail', 'seat_assignments_by_index', 'seat_data', 'credits', 'credits_total',
  'promo_applied', 'promo_validated_at', '_payment_method', '_miles_to_deplete',
  '_chose_refundable_installments', '_priced_traveler_count', '_returnFlight', '_searchParams',
  ...CART_DISCOUNT_FIELDS,
];

function offerOnly(offer) {
  if (!offer) return null;
  const next = { ...offer };
  for (const key of CART_ONLY_KEYS) delete next[key];
  return next;
}

/** "2027-05-06T07:00" → "2027-05-06". */
const dayOf = (value) => (String(value || '').match(/^(\d{4}-\d{2}-\d{2})/) || [])[1] || null;

/** /search prefilled with this trip: route, dates, the searched travelers and cabin. */
export function searchPathForItinerary(slices, searchParams = null) {
  const out = slices?.[0]?.segments || [];
  const back = slices?.[1]?.segments || [];
  const query = new URLSearchParams();
  const origin = out[0]?.origin?.iata_code;
  const destination = out[out.length - 1]?.destination?.iata_code;
  if (origin) query.set('origin', origin);
  if (destination) query.set('destination', destination);
  const departureDate = dayOf(out[0]?.departing_at);
  const returnDate = dayOf(back[0]?.departing_at);
  if (departureDate) query.set('departureDate', departureDate);
  if (returnDate) query.set('returnDate', returnDate);
  const travelers = Array.isArray(searchParams?.passengers) && searchParams.passengers.length
    ? searchParams.passengers
    : [{ type: 'adult' }];
  writePassengerParams(query, travelers);
  if (searchParams?.cabin_class) query.set('cabin', searchParams.cabin_class);
  return `/search?${query.toString()}`;
}

/** The booking token whose fare options are THESE flights, or null. A token
 *  for the outbound alone would price a round trip as a one-way. */
function fareOptionsToken({ selectedFlight, returnFlight, bookingOption, sliceCount }) {
  const stamped = bookingOption?._fare_for?.booking_token;
  if (stamped) return stamped;
  if (sliceCount === 1) return selectedFlight?.booking_token || null;
  // The return offer from the return search carries the round trip's token.
  // A return leg split out of a merged cart is a copy of the outbound: no.
  if (returnFlight && returnFlight.id && returnFlight.id !== selectedFlight?.id) {
    return returnFlight.booking_token || null;
  }
  return null;
}

/**
 * What a checkout restored without navigation should do.
 *   { action: 'price' }                         the saved fare checks out (or the flight has no fare step)
 *   { action: 'pick_fare', state, problems }    open fare options for these flights (FlightOptions nav state)
 *   { action: 'search', path, problems }        search again, prefilled (/search?…)
 *   { action: 'none' }                          nothing to restore
 */
export function restoredCheckoutPlan({ selectedFlight, returnFlight = null, bookingOption = null, searchParams = null, paymentMethod = 'installments' } = {}) {
  if (!selectedFlight) return { action: 'none' };
  const slices = itinerarySlices(selectedFlight, returnFlight);
  if (!bookingOption && !flightNeedsChosenFare(selectedFlight)) return { action: 'price' };

  const check = bookingOption
    ? checkFareOptionIntegrity({ bookingOption, slices })
    : { ok: false, problems: [{ code: 'no_fare', leg: null }] };
  if (check.ok) return { action: 'price' };

  const token = fareOptionsToken({ selectedFlight, returnFlight, bookingOption, sliceCount: slices.length });
  const searchedSlices = Array.isArray(searchParams?.slices) ? searchParams.slices.length : 0;
  const knowsTravelers = Array.isArray(searchParams?.passengers) && searchParams.passengers.length > 0;
  if (token && knowsTravelers && searchedSlices === slices.length) {
    const outbound = offerOnly(slices.length > 1 && selectedFlight.slices?.length > 1
      ? { ...selectedFlight, slices: [selectedFlight.slices[0]] }
      : selectedFlight);
    const back = slices.length > 1 ? offerOnly(returnFlight) : null;
    return {
      action: 'pick_fare',
      problems: check.problems,
      state: {
        bookingToken: token,
        searchParams,
        selectedFlight: outbound,
        returnFlight: back,
        paymentMethod: withoutRetiredMilesMethod(paymentMethod) || 'installments',
        tripMode: 'flight',
        packageType: 'flight',
      },
    };
  }
  return { action: 'search', problems: check.problems, path: searchPathForItinerary(slices, searchParams) };
}

/** What the customer is told on the way: fares change, so they pick it again. */
export const RESTORE_PICK_FARE_MESSAGE = 'Fares change often. Please choose your fare again to see today’s price.';
export const RESTORE_SEARCH_MESSAGE = 'Fares change often. Search again to see today’s price for this trip.';
